<template>
  <div
    v-if="layout === 'dropdown'"
    class="grid max-h-[min(32rem,70vh)] grid-cols-[15rem_minmax(0,1fr)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
  >
    <div class="overflow-y-auto border-r border-slate-100 bg-slate-50/80 p-2">
      <p v-if="!categories.length" class="px-3 py-6 text-sm text-slate-500">
        Nu sunt categorii de afișat.
      </p>
      <button
        v-for="category in categories"
        :key="category.id"
        type="button"
        class="flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left"
        :class="active?.id === category.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-700 hover:bg-white/80'"
        @mouseenter="activeId = category.id"
        @focus="activeId = category.id"
        @click="go(category)"
      >
        <span class="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white ring-1 ring-slate-200">
          <img
            v-if="category.image"
            :src="mediaUrl(category.image)"
            :alt="category.name"
            class="h-full w-full object-cover"
          >
          <span v-else class="text-xs font-semibold text-slate-400">{{ initial(category.name) }}</span>
        </span>
        <span class="min-w-0">
          <span class="block truncate text-sm font-medium">{{ category.name }}</span>
          <span v-if="category.productCount" class="block text-[11px] text-slate-400">
            {{ category.productCount }} produse
          </span>
        </span>
      </button>
    </div>

    <div class="overflow-y-auto p-4">
      <div v-if="active" class="mb-3 flex items-center justify-between gap-3">
        <p class="text-sm font-semibold text-slate-900">{{ active.name }}</p>
        <NuxtLink
          :to="shopPath(`/categorii/${active.slug}`)"
          class="text-sm font-medium text-brand-700 hover:underline"
          @click="emit('navigate')"
        >
          Vezi tot
        </NuxtLink>
      </div>
      <div v-if="cards.length" class="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
        <NuxtLink
          v-for="category in cards"
          :key="category.id"
          :to="shopPath(`/categorii/${category.slug}`)"
          class="group rounded-xl p-2 hover:bg-slate-50"
          @click="emit('navigate')"
        >
          <span class="flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-slate-100">
            <img
              v-if="category.image"
              :src="mediaUrl(category.image)"
              :alt="category.name"
              class="h-full w-full object-cover transition duration-300 group-hover:scale-105"
            >
            <span v-else class="text-lg font-semibold text-slate-400">{{ initial(category.name) }}</span>
          </span>
          <span class="mt-2 block text-center text-sm font-medium text-slate-800">{{ category.name }}</span>
        </NuxtLink>
      </div>
      <div v-else-if="active" :key="`products-${active.id}`">
        <p v-if="loadingId === active.id" class="py-8 text-center text-sm text-slate-500">
          Se încarcă produsele...
        </p>
        <div v-else-if="productsFor(active.id).length" class="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
          <NuxtLink
            v-for="product in productsFor(active.id)"
            :key="product.id"
            :to="shopPath(`/produse/${product.slug}`)"
            class="group rounded-xl p-2 hover:bg-slate-50"
            @click="emit('navigate')"
          >
            <span class="flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-slate-100">
              <img
                v-if="productImage(product)"
                :src="mediaUrl(productImage(product))"
                :alt="product.name"
                class="h-full w-full object-cover transition duration-300 group-hover:scale-105"
              >
              <span v-else class="text-lg font-semibold text-slate-400">{{ initial(product.name) }}</span>
            </span>
            <span class="mt-2 block text-center text-sm font-medium text-slate-800">{{ product.name }}</span>
          </NuxtLink>
        </div>
        <p v-else class="py-8 text-center text-sm text-slate-500">
          Nu sunt produse în această categorie.
        </p>
      </div>
    </div>
  </div>

  <div v-else class="border-b border-slate-200 pb-3">
    <div v-if="!drilled" class="space-y-1">
      <p class="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Categorii</p>
      <p v-if="!categories.length" class="px-3 py-2 text-sm text-slate-500">Nu sunt categorii de afișat.</p>
      <button
        v-for="category in categories"
        :key="category.id"
        type="button"
        class="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-slate-50"
        @click="openMobile(category)"
      >
        <span class="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
          <img
            v-if="category.image"
            :src="mediaUrl(category.image)"
            :alt="category.name"
            class="h-full w-full object-cover"
          >
          <span v-else class="text-sm font-semibold text-slate-400">{{ initial(category.name) }}</span>
        </span>
        <span class="min-w-0 flex-1 truncate text-base font-medium text-slate-800">{{ category.name }}</span>
        <UIcon
          v-if="category.children?.length"
          name="i-heroicons-chevron-right"
          class="size-4 shrink-0 text-slate-400"
        />
      </button>
    </div>

    <div v-else-if="drilled">
      <button
        type="button"
        class="mb-2 flex items-center gap-1 px-2 text-sm font-medium text-slate-600"
        @click="drilledId = null"
      >
        <UIcon name="i-heroicons-arrow-left" class="size-4" />
        Categorii
      </button>
      <NuxtLink
        :to="shopPath(`/categorii/${drilled.slug}`)"
        class="mb-3 block px-2 text-base font-semibold text-slate-900"
        @click="emit('navigate')"
      >
        {{ drilled.name }}
      </NuxtLink>
      <div v-if="drilled.children?.length" class="grid grid-cols-2 gap-2">
        <NuxtLink
          v-for="category in drilled.children"
          :key="category.id"
          :to="shopPath(`/categorii/${category.slug}`)"
          class="rounded-xl p-1.5 hover:bg-slate-50"
          @click="emit('navigate')"
        >
          <span class="flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-slate-100">
            <img
              v-if="category.image"
              :src="mediaUrl(category.image)"
              :alt="category.name"
              class="h-full w-full object-cover"
            >
            <span v-else class="text-base font-semibold text-slate-400">{{ initial(category.name) }}</span>
          </span>
          <span class="mt-1.5 block text-center text-xs font-medium text-slate-800">{{ category.name }}</span>
        </NuxtLink>
      </div>
      <div v-else :key="`mobile-products-${drilled.id}`">
        <p v-if="loadingId === drilled.id" class="py-6 text-center text-sm text-slate-500">
          Se încarcă produsele...
        </p>
        <div v-else-if="productsFor(drilled.id).length" class="grid grid-cols-2 gap-2">
          <NuxtLink
            v-for="product in productsFor(drilled.id)"
            :key="product.id"
            :to="shopPath(`/produse/${product.slug}`)"
            class="rounded-xl p-1.5 hover:bg-slate-50"
            @click="emit('navigate')"
          >
            <span class="flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-slate-100">
              <img
                v-if="productImage(product)"
                :src="mediaUrl(productImage(product))"
                :alt="product.name"
                class="h-full w-full object-cover"
              >
              <span v-else class="text-base font-semibold text-slate-400">{{ initial(product.name) }}</span>
            </span>
            <span class="mt-1.5 block text-center text-xs font-medium text-slate-800">{{ product.name }}</span>
          </NuxtLink>
        </div>
        <p v-else class="py-6 text-center text-sm text-slate-500">
          Nu sunt produse în această categorie.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Category, Product } from '~/types'

const props = defineProps<{
  categories: Category[]
  layout: 'dropdown' | 'stack'
}>()

const emit = defineEmits<{ navigate: [] }>()

const shopPath = useShopPath()
const { mediaUrl } = useMediaUrl()
const { get } = useApi()

const activeId = ref<number | null>(null)
const drilledId = ref<number | null>(null)
const productsByCategory = ref<Record<number, Product[]>>({})
const loadingId = ref<number | null>(null)

const active = computed(() =>
  props.categories.find((category) => category.id === activeId.value) || props.categories[0] || null,
)

const cards = computed(() => active.value?.children?.length ? active.value.children : [])

const drilled = computed(() =>
  props.categories.find((category) => category.id === drilledId.value) || null,
)

watch(() => props.categories, (list) => {
  if (props.layout === 'dropdown' && list.length && !list.some((category) => category.id === activeId.value)) {
    activeId.value = list[0].id
  }
}, { immediate: true })

watch(active, (category) => {
  if (props.layout === 'dropdown') void ensureProducts(category)
}, { immediate: true })

watch(drilled, (category) => {
  if (props.layout === 'stack') void ensureProducts(category)
})

function initial(name: string) {
  return name.trim().charAt(0).toUpperCase() || '•'
}

function productsFor(categoryId: number) {
  return productsByCategory.value[categoryId] || []
}

function productImage(product: Product) {
  return product.images?.find((image) => image.isPrimary && !image.is360)?.url
    || product.images?.find((image) => image.url && !image.is360)?.url
    || ''
}

function pickRandom<T>(items: T[], count: number): T[] {
  const copy = [...items]
  for (let index = copy.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1))
    const current = copy[index]
    copy[index] = copy[swapIndex]
    copy[swapIndex] = current
  }
  return copy.slice(0, count)
}

async function ensureProducts(category: Category | null) {
  if (!category || category.children?.length) return
  if (productsByCategory.value[category.id] || loadingId.value === category.id) return
  loadingId.value = category.id
  try {
    const response = await get<Product[]>(`/products?categoryId=${category.id}&limit=24`)
    const list = response.success && response.data ? response.data : []
    productsByCategory.value = {
      ...productsByCategory.value,
      [category.id]: pickRandom(list, 8),
    }
  } catch {
    productsByCategory.value = {
      ...productsByCategory.value,
      [category.id]: [],
    }
  } finally {
    if (loadingId.value === category.id) loadingId.value = null
  }
}

function go(category: Category) {
  emit('navigate')
  void navigateTo(shopPath(`/categorii/${category.slug}`))
}

function openMobile(category: Category) {
  drilledId.value = category.id
}
</script>
